import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '@env/environment';

const LOG_SERVER_PORT = 3399;
const PROJECT = 'webapp';
const LEVELS = ['log', 'info', 'warn', 'error', 'debug'] as const;
const MAX_QUEUE = 500;
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 8000;
type Level = (typeof LEVELS)[number];

interface LogPayload {
  project: string;
  level: Level;
  message: string;
  args: unknown[];
  timestamp: string;
  url: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  userAgent: string;
}

/** Host reachable from the TCPOS SCO WebView/emulator (page served via 10.0.2.2). */
function resolveLogServerWsUrl(): string {
  const host = environment.name === 'local-tcpos-sco' ? '10.0.2.2' : 'localhost';
  return `ws://${host}:${LOG_SERVER_PORT}/logs`;
}

function formatArg(a: unknown): string {
  if (typeof a === 'string') {
    return a;
  }
  if (a instanceof Error) {
    return a.stack ?? a.message;
  }
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

function serializeArgs(args: unknown[]): unknown[] {
  return args.map(a => {
    if (a instanceof Error) {
      return { __type: 'Error', message: a.message, stack: a.stack };
    }
    try {
      JSON.stringify(a);
      return a;
    } catch {
      return String(a);
    }
  });
}

@Injectable({ providedIn: 'root' })
export class ConsoleInterceptorService {
  private readonly document = inject(DOCUMENT);
  private readonly originals: Record<Level, (...args: unknown[]) => void> = {} as never;
  private socket: WebSocket | null = null;
  private queue: LogPayload[] = [];
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  private connecting = false;

  initialize(): void {
    const win = this.document.defaultView;
    if (!win) return;

    this.connect(win);

    for (const level of LEVELS) {
      const original = win.console[level].bind(win.console);
      this.originals[level] = original;

      win.console[level] = (...args: unknown[]) => {
        original(...args);
        queueMicrotask(() => this.sendLog(level, args, win));
      };
    }
  }

  private connect(win: Window): void {
    if (this.connecting || this.socket?.readyState === WebSocket.OPEN || this.reconnectTimer) {
      return;
    }
    this.connecting = true;
    try {
      const socket = new WebSocket(resolveLogServerWsUrl());
      this.socket = socket;

      socket.addEventListener('open', () => {
        this.connecting = false;
        this.reconnectAttempt = 0;
        this.flushQueue();
      });

      socket.addEventListener('close', () => {
        this.connecting = false;
        this.socket = null;
        this.scheduleReconnect(win);
      });

      socket.addEventListener('error', () => {
        socket.close();
      });
    } catch {
      this.connecting = false;
      this.scheduleReconnect(win);
    }
  }

  private scheduleReconnect(win: Window): void {
    if (this.reconnectTimer) return;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.reconnectAttempt, RECONNECT_MAX_MS);
    this.reconnectAttempt += 1;
    this.reconnectTimer = win.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(win);
    }, delay);
  }

  private flushQueue(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN || this.queue.length === 0) {
      return;
    }
    const batch = this.queue;
    this.queue = [];
    try {
      this.socket.send(JSON.stringify(batch.length === 1 ? batch[0] : batch));
    } catch {
      this.queue = batch.concat(this.queue).slice(-MAX_QUEUE);
    }
  }

  private enqueue(entry: LogPayload): void {
    this.queue.push(entry);
    if (this.queue.length > MAX_QUEUE) {
      this.queue = this.queue.slice(-MAX_QUEUE);
    }
  }

  private sendLog(level: Level, args: unknown[], win: Window): void {
    try {
      const entry: LogPayload = {
        project: PROJECT,
        level,
        message: args.map(formatArg).join(' '),
        args: serializeArgs(args),
        timestamp: new Date().toISOString(),
        url: win.location.href,
        screenWidth: win.screen.width,
        screenHeight: win.screen.height,
        viewportWidth: win.innerWidth,
        viewportHeight: win.innerHeight,
        userAgent: win.navigator.userAgent,
      };

      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify(entry));
        return;
      }

      this.enqueue(entry);
      this.connect(win);
    } catch {
      // never throw from the interceptor
    }
  }
}
